import { useEffect, useMemo, useState, useCallback } from "react";
import { VISITS } from "./data.js";
import { fetchCovers, fetchManifest, photoUrl } from "./api.js";
import { listDriveFolder } from "./driveSource.js";
import Placeholder from "./components/Placeholder.jsx";
import Detail from "./components/Detail.jsx";

// 把 R2 manifest photo 標註 source 並把 thumb/medium/original 轉成完整 URL
function normalizeR2Photo(p) {
  return {
    ...p,
    source: "r2",
    thumb: photoUrl(p.thumb),
    medium: photoUrl(p.medium),
    original: photoUrl(p.original),
  };
}

export default function App() {
  const sorted = useMemo(() => [...VISITS].sort((a, b) => b.date.localeCompare(a.date)), []);
  const [activeId, setActiveId] = useState(null);
  const [manifest, setManifest] = useState({ visits: {}, version: 1, updatedAt: null });
  const [drivePhotosByVisit, setDrivePhotosByVisit] = useState({}); // { visitId: photo[] }
  const [covers, setCovers] = useState({}); // { visitId: photoId }

  const loadManifest = useCallback(async () => {
    const m = await fetchManifest();
    setManifest(m);
  }, []);

  const loadCovers = useCallback(async () => {
    setCovers(await fetchCovers());
  }, []);

  // 一次性把所有有 driveFolderId 的 visit 都拉一次 Drive
  const loadDrive = useCallback(async () => {
    const results = await Promise.all(
      VISITS.filter((v) => v.driveFolderId).map(async (v) => {
        const photos = await listDriveFolder(v.driveFolderId);
        return [v.id, photos];
      }),
    );
    const map = Object.fromEntries(results);
    setDrivePhotosByVisit(map);
  }, []);

  useEffect(() => {
    loadManifest();
    loadDrive();
    loadCovers();
  }, [loadManifest, loadDrive, loadCovers]);

  // Merge R2 manifest + Drive，依 visit_id 合併、用 photo id 去重、按 exifDate/uploadedAt 排序
  const mergedPhotosByVisit = useMemo(() => {
    const out = {};
    for (const v of VISITS) {
      const r2 = (manifest.visits?.[v.id]?.photos || []).map(normalizeR2Photo);
      const drive = drivePhotosByVisit[v.id] || [];
      const all = [...r2, ...drive];
      // 按 EXIF 拍攝時間（fallback uploadedAt）排序
      all.sort((a, b) => {
        const aT = new Date(a.exifDate || a.uploadedAt || 0).getTime();
        const bT = new Date(b.exifDate || b.uploadedAt || 0).getTime();
        return aT - bT;
      });
      out[v.id] = all;
    }
    return out;
  }, [manifest, drivePhotosByVisit]);

  const totalPhotos = Object.values(mergedPhotosByVisit).reduce((s, arr) => s + arr.length, 0);
  const industries = new Set();
  VISITS.forEach((v) => v.industries.forEach((t) => industries.add(t)));

  const activeVisit = sorted.find((v) => v.id === activeId);
  const activeIdx = sorted.findIndex((v) => v.id === activeId);
  const prev = activeIdx > 0 ? sorted[activeIdx - 1] : null;
  const next = activeIdx >= 0 && activeIdx < sorted.length - 1 ? sorted[activeIdx + 1] : null;

  useEffect(() => {
    document.body.style.overflow = activeVisit ? "hidden" : "";
    if (activeVisit) window.scrollTo(0, 0);
    return () => { document.body.style.overflow = ""; };
  }, [activeVisit]);

  const featured = sorted.slice(0, 3);

  const [refreshing, setRefreshing] = useState(false);
  async function refreshAll() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([loadManifest(), loadDrive(), loadCovers()]);
    } finally {
      setRefreshing(false);
    }
  }

  function coverThumbFor(visitId) {
    const photos = mergedPhotosByVisit[visitId];
    if (!photos || photos.length === 0) return null;
    const coverId = covers[visitId];
    if (coverId) {
      const chosen = photos.find((p) => p.id === coverId);
      if (chosen) return chosen.thumb;
    }
    return photos[0].thumb;
  }

  return (
    <div className="acadB">
      {/* Logo bar */}
      <header className="nav">
        <div className="nav-inner">
          <div className="nav-logo">
            <img src="/ncku-emba-logo.jpeg" alt="國立成功大學 EMBA" />
            <div className="class-badge">
              <span className="cb-en">CLASS OF E118</span>
              <span className="cb-zh">EMBA 第 E118 級</span>
            </div>
          </div>
          <nav className="nav-links">
            <a href="#timeline">時間軸</a>
            <a href="#index">索引</a>
            <a href="#featured">特色參訪</a>
            <button
              className="nav-refresh"
              onClick={refreshAll}
              disabled={refreshing}
              title="重新拉 Drive + R2 manifest"
            >
              {refreshing ? "↻ 同步中…" : "↻ 重新整理"}
            </button>
          </nav>
        </div>
      </header>

      <div className="class-banner">
        <div className="class-banner-inner">
          <span>National Cheng Kung University · EMBA · <span className="bn-gold">Class of E118</span></span>
          <span>Field Study Archive — Vol. 01</span>
          <span>2025 — 2026</span>
        </div>
      </div>

      {/* HERO */}
      <section className="heroB">
        <div className="heroB-eyebrow">NCKU EMBA · CLASS E118 · FIELD STUDY ARCHIVE</div>
        <h1>E118 走進<br />產業現場的<span className="gold">紀錄</span>。</h1>
        <div className="heroB-bottom">
          <p className="heroB-deck">
            成功大學 EMBA E118 級職業參訪歷程典藏。我們循著時間軸走訪台灣的關鍵產業，
            把每一場現場對話、每一張影像紀錄完整保存，作為班級共同的學術資產與企業記憶。
          </p>
          <div className="heroB-stats">
            <div className="heroB-stat">
              <div className="num">{VISITS.length}</div>
              <div className="lbl">Field Visits</div>
            </div>
            <div className="heroB-stat">
              <div className="num">{totalPhotos}</div>
              <div className="lbl">Photographs</div>
            </div>
            <div className="heroB-stat">
              <div className="num">{VISITS.length}</div>
              <div className="lbl">Companies</div>
            </div>
            <div className="heroB-stat">
              <div className="num">{VISITS[0]?.year || "—"}</div>
              <div className="lbl">Period</div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="index-bar" id="timeline">
        <div className="index-bar-head">
          <h2>Timeline</h2>
          <span className="count">{VISITS.length} entries</span>
        </div>
        <div className="timeline">
          {sorted.map((v, i) => (
            <div className="tl-tick" key={v.id} onClick={() => setActiveId(v.id)}>
              <div className="tl-num">No. {String(i + 1).padStart(2, "0")}</div>
              <div className="tl-date">{v.dateLabel}</div>
              <div className="tl-name">{v.companyShort}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Index list */}
      <section className="indexlist" id="index">
        <div className="indexlist-head">
          <h2>Index of Visits</h2>
          <span className="right">Sorted · Newest First</span>
        </div>
        {sorted.map((v, i) => (
          <div className="ix-row" key={v.id} onClick={() => setActiveId(v.id)}>
            <span className="ix-num">{String(i + 1).padStart(2, "0")}</span>
            <span className="ix-date">{v.dateLabel}</span>
            <span className="ix-name">{v.companyShort}</span>
            <span className="ix-loc">{v.location}</span>
            <div className="ix-tags">
              {v.industries.slice(0, 3).map((t) => <span className="tag" key={t}>{t}</span>)}
            </div>
            <span className="ix-arrow">→</span>
          </div>
        ))}
      </section>

      {/* Featured */}
      <section className="featured" id="featured">
        <div className="featured-head">
          <span className="gold-mark">— Featured</span>
          <h2>近期參訪</h2>
        </div>
        <div className="featured-grid">
          {featured.map((v, i) => {
            const cover = coverThumbFor(v.id);
            return (
              <div className={`feat-card ${i === 0 ? "lead" : ""}`} key={v.id} onClick={() => setActiveId(v.id)}>
                <div className="feat-img">
                  <span className="feat-num">No. {String(sorted.findIndex((x) => x.id === v.id) + 1).padStart(2, "0")}</span>
                  {cover ? (
                    <img src={cover} alt={v.companyShort} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <Placeholder kind={v.cover} label={v.companyShort} />
                  )}
                </div>
                <div className="feat-eyebrow">{v.industries[0]}</div>
                <h3 className="feat-name">{v.companyShort}</h3>
                <div className="feat-meta">
                  <span>{v.dateLabel}</span>
                  <span className="dot"></span>
                  <span>{v.location}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pull quote */}
      <div className="pullB">
        <div className="gold-line"></div>
        <blockquote>
          讀萬卷書，行萬里路。<br />
          每一次參訪，都是一次把書本翻成現場的旅程。
        </blockquote>
        <div className="from">— EMBA E118 全體同學</div>
      </div>

      {/* Footer */}
      <footer className="footB">
        <div className="footB-inner">
          <div className="footB-logo">
            <img src="/ncku-emba-logo.jpeg" alt="NCKU EMBA" />
            <div className="fb-badge">
              <span className="a">CLASS OF E118</span>
              <span className="b">EMBA 第 E118 級</span>
            </div>
          </div>
          <div className="footB-meta">
            <span>國立成功大學 EMBA 高階管理碩士在職專班</span>
            <span>Class of E118 · Field Study Archive</span>
            <span>© 2026 — Maintained by E118</span>
          </div>
        </div>
      </footer>

      {activeVisit && (
        <Detail
          visit={activeVisit}
          prev={prev}
          next={next}
          onClose={() => setActiveId(null)}
          onNav={setActiveId}
          photos={mergedPhotosByVisit[activeVisit.id] || []}
          coverPhotoId={covers[activeVisit.id] || null}
          onUploaded={() => { loadManifest(); loadDrive(); }}
          onCoverChanged={(photoId) => setCovers((prev) => ({ ...prev, [activeVisit.id]: photoId }))}
        />
      )}
    </div>
  );
}
